import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Badge, Banner, Button, Dialog, Empty, Loader, cn } from '@cloudflare/kumo'
import { PrinterIcon, XIcon } from '@phosphor-icons/react'
import { listBuildingPrinters, type BuildingPrinter } from '../../api/printJobs'
import { useTranslation } from '../../i18n'
import { printerReachability } from '../../lib/printerConnection'

interface Props {
  open: boolean
  buildingId: string | null
  onPick: (printerId: string) => void
  onClose: () => void
}

/**
 * Printer selector built on Kumo `Dialog`. Opens programmatically via the
 * controlled `open` prop (no trigger element) so it can be reused in the
 * QR-release flow where the caller decides when to show it. Kumo handles the
 * scrim, panel background, focus trap and Esc-to-close.
 */
export function PrinterPicker({ open, buildingId, onPick, onClose }: Props) {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const [printers, setPrinters] = useState<BuildingPrinter[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !buildingId) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const token = await getAccessTokenSilently()
        const items = await listBuildingPrinters({ token, buildingId })
        if (!cancelled) setPrinters(items)
      } catch {
        if (!cancelled) setError(t('common.error'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, buildingId, getAccessTokenSilently, t])

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <Dialog size="sm" className="p-4">
        <div className="mb-3 flex items-center justify-between gap-4">
          <Dialog.Title className="text-lg font-medium">{t('print.picker.title')}</Dialog.Title>
          <Dialog.Close
            aria-label={t('common.close')}
            render={(props) => (
              <Button
                {...props}
                variant="secondary"
                shape="square"
                size="sm"
                icon={<XIcon />}
                aria-label={t('common.close')}
              />
            )}
          />
        </div>

        {loading && (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        )}
        {error && <Banner variant="error">{error}</Banner>}
        {!loading && !error && printers.length === 0 && (
          <Empty
            size="sm"
            icon={<PrinterIcon size={32} className="text-kumo-inactive" />}
            title={t('print.picker.empty')}
          />
        )}
        {!loading && !error && printers.length > 0 && (
          <ul className="flex flex-col gap-2">
            {printers.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onPick(p.id)}
                  className="flex w-full items-center justify-between rounded-md border border-kumo-line p-3 text-left hover:border-kumo-interact"
                  data-testid={`picker-printer-${p.id}`}
                >
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-sm text-kumo-subtle">{p.productName ?? ''}</div>
                  </div>
                  {printerReachability(p.epsonConnected, p.connectionCheckedAt) === 'offline' ? (
                    <Badge variant="error">{t('print.picker.offline')}</Badge>
                  ) : (
                    // Green only on a recent "connected" probe: "seen once" says
                    // nothing about now (the printer was offline for months).
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full',
                        printerReachability(p.epsonConnected, p.connectionCheckedAt) === 'online'
                          ? 'bg-kumo-success'
                          : 'bg-kumo-fill',
                      )}
                      aria-label={
                        printerReachability(p.epsonConnected, p.connectionCheckedAt) === 'online'
                          ? t('print.picker.statusOnline')
                          : t('print.picker.statusUnknown')
                      }
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </Dialog.Root>
  )
}
